
import React, { useState, useRef, useEffect, useMemo } from 'react';
import type { View, Notification, Theme, AppPage } from '../../types';
import { ALL_PAGES } from '../../types';
import { Icons } from '../ui/Icons';
import NotificationBell from '../notifications/NotificationBell';
import NotificationPanel from '../notifications/NotificationPanel';

interface HeaderProps {
    isLoggedIn: boolean;
    isAdmin: boolean;
    userName: string;
    onLogout: () => void;
    view: View;
    setView: (view: View) => void;
    isSaving: boolean;
    notifications: Notification[];
    onMarkNotificationAsRead: (id: string) => void;
    onMarkAllNotificationsAsRead: () => void;
    onClearNotifications: () => void;
    theme: Theme;
    setTheme: (theme: Theme) => void;
    propertyName: string;
    allowedPages?: AppPage[];
}

export const ThemeToggle: React.FC<{ theme: Theme, setTheme: (theme: Theme) => void }> = ({ theme, setTheme }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const themes: { name: Theme, icon: React.ReactNode }[] = [
        { name: 'light', icon: <Icons.Sun className="h-5 w-5" /> },
        { name: 'dark', icon: <Icons.Moon className="h-5 w-5" /> },
        { name: 'system', icon: <Icons.Monitor className="h-5 w-5" /> },
    ];

    const currentThemeIcon = themes.find(t => t.name === theme)?.icon;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={dropdownRef} className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500"
                aria-label="Toggle theme"
            >
                {currentThemeIcon}
            </button>
            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-36 bg-white dark:bg-slate-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-slate-700 z-50">
                    <div className="py-1">
                        {themes.map(({ name, icon }) => (
                            <button
                                key={name}
                                onClick={() => {
                                    setTheme(name);
                                    setIsOpen(false);
                                }}
                                className={`w-full flex items-center gap-2 px-4 py-2 text-sm ${
                                    theme === name ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                                } hover:bg-gray-100 dark:hover:bg-slate-700`}
                            >
                                {icon}
                                <span className="capitalize">{name === 'system' ? 'Reception' : name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};


const Header: React.FC<HeaderProps> = ({ 
    isLoggedIn, 
    isAdmin, 
    userName, 
    onLogout, 
    view, 
    setView, 
    isSaving, 
    notifications, 
    onMarkNotificationAsRead, 
    onMarkAllNotificationsAsRead, 
    onClearNotifications,
    theme, 
    setTheme,
    propertyName,
    allowedPages
}) => {
    const navItems = [
        { page: 'scheduler', label: 'Scheduler', icon: <Icons.Calendar className="h-4 w-4" /> },
        { page: 'schedulerPreview', label: 'Schedule', icon: <Icons.Calendar className="h-4 w-4" /> },
        { page: 'staffList', label: 'Staff', icon: <Icons.Users className="h-4 w-4" /> },
        { page: 'taskManager', label: 'Tasks', icon: <Icons.CheckSquare className="h-4 w-4" /> },
        { page: 'attendance', label: 'Attendance', icon: <Icons.Clock className="h-4 w-4" /> },
        { page: 'timeOff', label: 'Time Off', icon: <Icons.Plane className="h-4 w-4" /> },
        { page: 'parking', label: 'Parking', icon: <Icons.Car className="h-4 w-4" /> },
        { page: 'shuttle', label: 'Shuttle', icon: <Icons.Bus className="h-4 w-4" /> },
        { page: 'maintenance', label: 'Maintenance', icon: <Icons.Tool className="h-4 w-4" /> },
        { page: 'accessManager', label: 'Access Manager', icon: <Icons.Key className="h-4 w-4" /> },
        { page: 'reports', label: 'Reports', icon: <Icons.BarChart className="h-4 w-4" /> },
        { page: 'memos', label: 'Memos', icon: <Icons.FileText className="h-4 w-4" /> },
        { page: 'settings', label: 'Settings', icon: <Icons.Settings className="h-4 w-4" /> },
        { page: 'help', label: 'Help', icon: <Icons.HelpCircle className="h-4 w-4" /> },
    ];

    const filteredNavItems = useMemo(() => {
        if (!allowedPages) return navItems;
        
        // Filter by allowed pages
        let filtered = navItems.filter(item => allowedPages.includes(item.page as AppPage));
        
        // If both scheduler and schedulerPreview are allowed, prefer scheduler
        if (allowedPages.includes('scheduler') && allowedPages.includes('schedulerPreview')) {
            filtered = filtered.filter(item => item.page !== 'schedulerPreview');
        }
        
        return filtered;
    }, [allowedPages]);
    
    const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const notificationRef = useRef<HTMLDivElement>(null);
    const mobileMenuRef = useRef<HTMLDivElement>(null);

    const unreadCount = notifications.filter(n => !n.read).length;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
                setIsNotificationPanelOpen(false);
            }
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
                setIsMobileMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setDeferredPrompt(null);
        }
    };

    return (
        <header className="bg-white dark:bg-slate-800 shadow-sm dark:border-b dark:border-slate-700 sticky top-0 z-40">
            <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
                {/* Top Row: Logo & Profile */}
                <div className="flex justify-between items-center py-3">
                    <div className="flex items-center space-x-4">
                        {isAdmin && (
                            <div ref={mobileMenuRef} className="lg:hidden relative">
                                <button
                                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                    className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                                    aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                                >
                                    {isMobileMenuOpen ? (
                                        <Icons.X className="w-6 h-6 animate-in spin-in-90 duration-200" />
                                    ) : (
                                        <Icons.Menu className="w-6 h-6 animate-in fade-in duration-200" />
                                    )}
                                </button>
                                
                                {isMobileMenuOpen && (
                                    <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 dark:ring-slate-700 z-50 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                                        {filteredNavItems.map(item => (
                                            <button
                                                key={item.page}
                                                onClick={() => {
                                                    setView({ page: item.page as View['page'] });
                                                    setIsMobileMenuOpen(false);
                                                }}
                                                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                                                    view.page === item.page
                                                        ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/50'
                                                }`}
                                            >
                                                {item.icon}
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <img src="/icon.svg" alt="Logo" className="h-9 w-9" referrerPolicy="no-referrer" />
                            <div className="flex flex-col">
                                <span className="text-xl font-bold text-gray-900 dark:text-white leading-tight">Hotel Operations Hub</span>
                                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">{propertyName}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-2 sm:space-x-4">
                        {deferredPrompt && (
                            <button
                                onClick={handleInstallClick}
                                className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50 transition-colors"
                            >
                                <Icons.Download className="h-4 w-4" />
                                Install App
                            </button>
                        )}
                        {isSaving && (
                            <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                                <Icons.Loader className="h-4 w-4 animate-spin" />
                                <span>Saving...</span>
                            </div>
                        )}
                        
                        <ThemeToggle theme={theme} setTheme={setTheme} />

                        <div ref={notificationRef} className="relative">
                            <NotificationBell
                                unreadCount={unreadCount}
                                onClick={() => setIsNotificationPanelOpen(prev => !prev)}
                            />
                            {isNotificationPanelOpen && (
                                <NotificationPanel
                                    notifications={notifications}
                                    onMarkAsRead={onMarkNotificationAsRead}
                                    onMarkAllAsRead={() => {
                                        onMarkAllNotificationsAsRead();
                                        setIsNotificationPanelOpen(false);
                                    }}
                                    onClearAll={() => {
                                        onClearNotifications();
                                        setIsNotificationPanelOpen(false);
                                    }}
                                    onClose={() => setIsNotificationPanelOpen(false)}
                                />
                            )}
                        </div>
                        
                        <div className="w-px h-6 bg-gray-200 dark:bg-slate-700 hidden sm:block" />

                        <div className="flex items-center gap-3">
                            <div className="text-right hidden sm:block">
                                <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{userName}</p>
                                <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{isAdmin ? 'Administrator' : 'Staff Member'}</p>
                            </div>
                            {isAdmin && (
                                <button
                                    onClick={() => setView({ page: 'myProfile' })}
                                    className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all"
                                    title="My Profile"
                                >
                                    <Icons.UserCog className="h-5 w-5" />
                                </button>
                            )}

                            <button
                                onClick={onLogout}
                                className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-all"
                                title="Logout"
                            >
                                <Icons.LogOut className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Bottom Row: Navigation */}
                <div className="hidden lg:block border-t border-gray-100 dark:border-slate-700/50 py-2">
                    <nav className="flex items-center gap-1 overflow-x-auto no-scrollbar">
                        {filteredNavItems.map(item => (
                            <button
                                key={item.page}
                                onClick={() => setView({ page: item.page as View['page'] })}
                                className={`flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-xl transition-all duration-200 whitespace-nowrap ${
                                    view.page === item.page
                                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700/50 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                            >
                                {item.icon}
                                {item.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>
        </header>
    );
};

export default Header;